import React, {createContext, useCallback, useContext, useRef} from 'react';

type ServerAwaitMetadataProviderContext = {
  setMetadataHandled: (handled: boolean) => void;
  waitUntilMetadataHandled: () => Promise<true> | undefined;
};

const ServerAwaitMetadataProviderContext =
  createContext<ServerAwaitMetadataProviderContext>({
    setMetadataHandled: () => {
      throw new Error(
        'Default value should never be used, make sure ServerAwaitMetadataProvider is used',
      );
    },
    waitUntilMetadataHandled: () => {
      throw new Error(
        'Default value should never be used, make sure ServerAwaitMetadataProvider is used',
      );
    },
  });

export const ServerAwaitMetadataProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const isMetadataHandled = useRef(true);

  const unblockPendingRequests = useRef<(value: true) => void>(() => void 0);

  const promiseToAwait = useRef(
    new Promise<true>(resolve => {
      unblockPendingRequests.current = resolve;
    }),
  ).current;

  const setMetadataHandled = useCallback(
    (handled: boolean) => {
      isMetadataHandled.current = handled;
      if (handled) {
        queueMicrotask(() => {
          unblockPendingRequests.current(true);
        });
      }
    },
    [unblockPendingRequests],
  );

  const waitUntilMetadataHandled = () => {
    if (isMetadataHandled.current) {
      Promise.resolve(true);
      return;
    }

    return promiseToAwait;
  };

  const value: ServerAwaitMetadataProviderContext = {
    setMetadataHandled,
    waitUntilMetadataHandled,
  };

  return (
    <ServerAwaitMetadataProviderContext.Provider value={value}>
      {children}
    </ServerAwaitMetadataProviderContext.Provider>
  );
};

export const useServerAwaitMetadata = () => {
  const {setMetadataHandled, waitUntilMetadataHandled} = useContext(
    ServerAwaitMetadataProviderContext,
  );

  return {
    setMetadataHandled,
    waitUntilMetadataHandled,
  };
};
